import type { CostLine, Deal } from '../lib/flip';
import { newId } from '../lib/ids';
import { NumberField } from './NumberField';

interface Props {
  deal: Deal;
  onChange: (d: Deal) => void;
}

/** All the numbers that decide one deal, in the order you meet them. */
export function DealForm({ deal, onChange }: Props) {
  const set = <K extends keyof Deal>(k: K, v: Deal[K]) => onChange({ ...deal, [k]: v });
  const setLine = (id: string, patch: Partial<CostLine>) =>
    set('repairs', deal.repairs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div className="deal-form">
      <fieldset>
        <legend>1. Buying</legend>
        <div className="grid">
          <NumberField label="Purchase price" prefix="£" value={deal.purchasePrice} onChange={(n) => set('purchasePrice', n)} />
          <NumberField label="Auction buyer's fee" suffix="%" value={deal.buyerFeePct} onChange={(n) => set('buyerFeePct', n)} hint="Percent of the price. 0 for a private sale." />
          <NumberField label="Fixed buying fees" prefix="£" value={deal.buyerFeeFixed} onChange={(n) => set('buyerFeeFixed', n)} hint="Entry or admin fees." />
          <NumberField label="Collection" prefix="£" value={deal.collection} onChange={(n) => set('collection', n)} hint="Fuel, train fare or recovery." />
        </div>
      </fieldset>

      <fieldset>
        <legend>2. Repairs and parts</legend>
        {deal.repairs.length === 0 && <p className="muted">No repairs yet. Add each job or part as its own line.</p>}
        {deal.repairs.map((r) => (
          <div className="repair-row" key={r.id}>
            <div className="field grow">
              <label htmlFor={`rl-${r.id}`}>What</label>
              <input id={`rl-${r.id}`} type="text" value={r.label} placeholder="e.g. front brake pads" onChange={(e) => setLine(r.id, { label: e.target.value })} />
            </div>
            <NumberField label="Cost" prefix="£" value={r.amount} onChange={(n) => setLine(r.id, { amount: n })} />
            <button type="button" className="btn small danger" aria-label={`Remove ${r.label || 'repair line'}`} onClick={() => set('repairs', deal.repairs.filter((x) => x.id !== r.id))}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="btn small" onClick={() => set('repairs', [...deal.repairs, { id: newId(), label: '', amount: 0 }])}>
          + Add repair
        </button>
        <div className="grid">
          <NumberField label="Extra allowance" suffix="%" value={deal.contingencyPct} onChange={(n) => set('contingencyPct', n)} hint="Repairs nearly always cost more than planned." />
        </div>
      </fieldset>

      <fieldset>
        <legend>3. Getting it ready</legend>
        <div className="grid">
          <NumberField label="MOT" prefix="£" value={deal.mot} onChange={(n) => set('mot', n)} hint="Test and any retest. 0 if it has a long MOT." />
          <NumberField label="Valet" prefix="£" value={deal.valet} onChange={(n) => set('valet', n)} />
          <NumberField label="History check" prefix="£" value={deal.historyCheck} onChange={(n) => set('historyCheck', n)} hint="HPI or similar. Always worth it." />
          <NumberField label="Road tax" prefix="£" value={deal.tax} onChange={(n) => set('tax', n)} hint="Only if you must tax it to drive it." />
        </div>
      </fieldset>

      <fieldset>
        <legend>4. Holding it</legend>
        <div className="grid">
          <NumberField label="Days until paid" value={deal.holdingDays} onChange={(n) => set('holdingDays', n)} hint="Buying to cash in the bank." />
          <NumberField label="Insurance per day" prefix="£" value={deal.insurancePerDay} onChange={(n) => set('insurancePerDay', n)} />
          <NumberField label="Storage per day" prefix="£" value={deal.storagePerDay} onChange={(n) => set('storagePerDay', n)} hint="0 if it sits on your drive." />
          <NumberField label="Cost of tied-up cash" suffix="% a year" value={deal.capitalCostPctPerYear} onChange={(n) => set('capitalCostPctPerYear', n)} hint="Finance interest, or what the cash could earn." />
        </div>
      </fieldset>

      <fieldset>
        <legend>5. Selling</legend>
        <div className="grid">
          <NumberField label="Sale price" prefix="£" value={deal.salePrice} onChange={(n) => set('salePrice', n)} hint="Be realistic: use sold prices, not asking prices." />
          <NumberField label="Advert fee" prefix="£" value={deal.adFee} onChange={(n) => set('adFee', n)} />
          <NumberField label="Selling fees" suffix="%" value={deal.saleFeePct} onChange={(n) => set('saleFeePct', n)} hint="Platform and payment fees." />
        </div>
      </fieldset>
    </div>
  );
}
